/* HPN NAD3 Extra Strength Buy Box — Clinician AI on mobile */
(() => {
  if (window.__hpnNad3ExtraStrengthClinicianAiInitialized) return;
  window.__hpnNad3ExtraStrengthClinicianAiInitialized = true;

  /* ---------------------------------------------------------------------------
   * Mobile only. Frontrow's ClinicianAI renders ONE real floating button
   * (iframe#iframe-modal-frontrow-gpt). The same iframe grows into the chat
   * window when tapped and shrinks back into the button when closed, keeping
   * the conversation.
   *
   * 1. Real button in our spots: while it is button-sized, we pin that real
   *    iframe onto the spot (main image corner / below the offer) that is on
   *    screen. It is anchored to the page (not the screen), so it scrolls with
   *    the page. When Frontrow opens the chat we step aside and let it go
   *    full-screen as usual; when it closes we pin it again.
   * 2. Fallback: if the real button is not there (script blocked, slow, ...),
   *    our look-alike pill shows instead and opens Frontrow's chat box as a
   *    bottom sheet.
   * 3. Other Frontrow floating elements are hidden on mobile when the section
   *    setting is on (visibility only, so they can still be measured).
   * ------------------------------------------------------------------------- */
  const SELECTOR_ROOT = '[data-hpn-nad3-extra-strength-buy-box]';
  const SLOT_SELECTOR = SELECTOR_ROOT + ' .hpn-nad3-extra-strength-buy-box__clinicianAiSlot';
  const REAL_SLOT_CLASS = 'hpn-nad3-extra-strength-buy-box__clinicianAiSlot--real';
  const SPOT_CLASS = 'hpn-nad3-extra-strength-buy-box__clinicianAiRealSpot';
  const HIDDEN_CLASS = 'hpn-nad3-extra-strength-buy-box-frontrow-launcher--hidden-mobile';
  const PARKED_CLASS = 'hpn-nad3-extra-strength-buy-box-frontrow-bubble--parked';
  const BODY_HIDE_CLASS = 'hpn-nad3-extra-strength-buy-box-hide-frontrow-floating';
  const OPEN_ATTR = 'data-hpn-nad3-extra-strength-buy-box-frontrow-open';
  const SHEET_CLASS = 'hpn-nad3-extra-strength-buy-box-clinician-ai-sheet';
  const SHEET_OPEN_CLASS = 'hpn-nad3-extra-strength-buy-box__clinicianAi--sheet';
  const MOBILE_QUERY = '(max-width: 899px)';

  // Inline properties we override on the real button while it sits in a spot.
  const PIN_PROPS = ['position', 'left', 'top', 'right', 'bottom', 'margin', 'z-index'];
  const PIN_Z_INDEX = '6';

  let hideFloating = false;
  let hasSlots = false;
  let observer = null;
  let resizeObserver = null;
  let rotateTimer = null;
  let syncPending = false;
  let placeQueued = false;
  const watched = new WeakSet();
  let openEls = new Set();

  // Real button state
  let bubble = null;
  let bubbleObserver = null;
  let bubbleSize = { w: 154, h: 49 };
  let hostSpot = null;
  const savedProps = {}; // Frontrow's own inline values for PIN_PROPS
  let pinned = false;

  // Fallback sheet state
  let sheetContainer = null;
  let sheetPlaceholder = null;
  let sheetClose = null;

  // Diagnostics: add ?hpn_fr_debug=1 to the page URL.
  const debugMessages = [];
  let debug = 0;
  try {
    const m = window.location.search.match(/[?&]hpn_fr_debug=(\d)/);
    debug = m ? Number(m[1]) : 0;
  } catch (e) {
    debug = 0;
  }

  const isMobile = () => window.matchMedia(MOBILE_QUERY).matches;

  /* ------------------------- Frontrow element detection ------------------------- */

  function isFrontrowish(el) {
    if (!el || el.nodeType !== 1) return false;
    const tag = el.tagName.toLowerCase();
    if (tag.indexOf('frontrow') !== -1) return true;
    const id = (el.id || '').toLowerCase();
    const cls = (typeof el.className === 'string' ? el.className : el.getAttribute('class') || '').toLowerCase();
    if (id.indexOf('frontrow') !== -1 || cls.indexOf('frontrow') !== -1) return true;
    if (tag === 'iframe' && (el.getAttribute('src') || '').toLowerCase().indexOf('frontrow') !== -1) return true;
    return false;
  }

  function isExcluded(el) {
    if (el.matches('.iframe-frontrow-gpt, .frontrow-gpt-container')) return true;
    if (el.closest('#frontrow-badge')) return true;
    if (el.closest('.' + SHEET_CLASS)) return true;
    if (el.closest('[data-hpn-nad3-extra-strength-buy-box-clinician-ai-trigger]')) return true;

    // Frontrow badges/stickers rendered inside the buy box are not the chat bubble,
    // except anything injected inside the chat container itself.
    const inRoot = el.closest(SELECTOR_ROOT);
    if (inRoot && !el.closest('.frontrow-gpt-container')) return true;

    return false;
  }

  function outermostFixed(el) {
    let node = el;
    let found = null;
    while (node && node !== document.body && node.nodeType === 1) {
      if (window.getComputedStyle(node).position === 'fixed') found = node;
      node = node.parentElement;
    }
    return found;
  }

  function findFloatingElements() {
    const results = new Set();
    const all = document.body ? document.body.getElementsByTagName('*') : [];

    for (let i = 0; i < all.length; i += 1) {
      const el = all[i];
      if (!isFrontrowish(el) || isExcluded(el)) continue;

      // Widget hosts appended straight to <body> (incl. custom elements / shadow DOM hosts).
      if (el.parentElement === document.body) {
        results.add(el);
        continue;
      }

      const fixed = outermostFixed(el);
      if (fixed) {
        if (!isExcluded(fixed)) results.add(fixed);
        continue;
      }

      // Fixed-position pieces nested inside a Frontrow wrapper.
      const inner = el.getElementsByTagName('*');
      for (let j = 0; j < inner.length; j += 1) {
        if (window.getComputedStyle(inner[j]).position === 'fixed') {
          results.add(inner[j]);
          break;
        }
      }
    }

    // The real button is handled by placement, make sure it is always listed.
    if (bubble && bubble.isConnected) results.add(bubble);

    return Array.from(results);
  }

  /* An element big enough to be the open chat window (not the small button). */
  function isChatWindowSize(el) {
    const rect = el.getBoundingClientRect();
    const vw = window.innerWidth || document.documentElement.clientWidth;
    const vh = window.innerHeight || document.documentElement.clientHeight;
    return rect.width >= vw * 0.6 && rect.height >= vh * 0.4;
  }

  function watchElement(el) {
    if (watched.has(el)) return;
    watched.add(el);
    if (resizeObserver) resizeObserver.observe(el);
    if (observer) observer.observe(el, { attributes: true, attributeFilter: ['style', 'class', 'hidden'] });
  }

  function syncFloating() {
    document.body.classList.toggle(BODY_HIDE_CLASS, hideFloating);

    findBubble();

    const found = findFloatingElements();
    const openNow = found.filter(isChatWindowSize);
    let newlyOpened = false;
    const useBubble = bubbleUsable();

    found.forEach((el) => {
      watchElement(el);
      const open = openNow.indexOf(el) !== -1;
      // Wrappers around the open chat window / the real button must stay visible,
      // or they pass their hidden state on.
      const holdsOpen = !open && openNow.some((o) => el.contains(o));
      const holdsBubble = useBubble && el.contains(bubble);

      if (open && !openEls.has(el)) newlyOpened = true;

      if (open || holdsOpen || holdsBubble) {
        el.setAttribute(OPEN_ATTR, '');
      } else {
        el.removeAttribute(OPEN_ATTR);
      }
      el.classList.toggle(HIDDEN_CLASS, hideFloating && !open && !holdsOpen && !holdsBubble);
    });

    openEls = new Set(openNow);

    // Frontrow just opened its chat window: our fallback sheet steps aside.
    if (newlyOpened && sheetContainer) closeSheet();

    queuePlace();

    if (debug) renderDebug(found);
    return found;
  }

  function scheduleSync() {
    if (syncPending) return;
    syncPending = true;
    window.setTimeout(() => {
      syncPending = false;
      syncFloating();
    }, 120);
  }

  function watchFloating() {
    if (!observer && window.MutationObserver) {
      observer = new MutationObserver(scheduleSync);
      observer.observe(document.body, { childList: true, subtree: true });
    }
    if (!resizeObserver && window.ResizeObserver) {
      resizeObserver = new ResizeObserver(scheduleSync);
      // Page layout changes (images loading etc.) move our spots.
      resizeObserver.observe(document.body);
    }
    window.addEventListener('resize', scheduleSync, { passive: true });
    window.addEventListener('scroll', queuePlace, { passive: true });
    syncFloating();
  }

  /* ---------------------- Real button pinned to our spots ---------------------- */

  function findBubble() {
    const el =
      document.getElementById('iframe-modal-frontrow-gpt') ||
      document.querySelector('iframe.iframe-modal-frontrow-gpt');

    if (el === bubble) return bubble;

    if (bubble) unpin();
    bubble = el || null;

    if (bubble) {
      if (bubbleObserver) bubbleObserver.disconnect();
      // React straight away when Frontrow restyles it (open / close).
      bubbleObserver = new MutationObserver(queuePlace);
      bubbleObserver.observe(bubble, { attributes: true, attributeFilter: ['style', 'class'] });
      if (window.ResizeObserver) new ResizeObserver(queuePlace).observe(bubble);
    }
    return bubble;
  }

  function bubbleUsable() {
    return !!(hasSlots && isMobile() && bubble && bubble.isConnected);
  }

  function getSpots() {
    const spots = [];
    document.querySelectorAll(SLOT_SELECTOR).forEach((slot) => {
      let spot = slot.querySelector('.' + SPOT_CLASS);
      if (!spot) {
        spot = document.createElement('span');
        spot.className = SPOT_CLASS;
        spot.setAttribute('aria-hidden', 'true');
        slot.appendChild(spot);
      }
      spot.style.width = bubbleSize.w + 'px';
      spot.style.height = bubbleSize.h + 'px';
      spots.push(spot);
    });
    return spots;
  }

  function setRealSlots(on) {
    document.querySelectorAll(SLOT_SELECTOR).forEach((slot) => slot.classList.toggle(REAL_SLOT_CLASS, on));
  }

  function visibleRatio(el) {
    if (!el.offsetParent && window.getComputedStyle(el).position !== 'fixed') return 0;
    const r = el.getBoundingClientRect();
    if (!r.width || !r.height) return 0;
    const vw = window.innerWidth || document.documentElement.clientWidth;
    const vh = window.innerHeight || document.documentElement.clientHeight;
    const w = Math.max(0, Math.min(r.right, vw) - Math.max(r.left, 0));
    const h = Math.max(0, Math.min(r.bottom, vh) - Math.max(r.top, 0));
    return (w * h) / (r.width * r.height);
  }

  function setImportant(el, prop, value) {
    if (el.style.getPropertyValue(prop) !== value || el.style.getPropertyPriority(prop) !== 'important') {
      el.style.setProperty(prop, value, 'important');
    }
  }

  /* Remember Frontrow's own value for each property we override. Frontrow
     writing a property again clears our "important" flag, so anything without
     it is Frontrow's latest value. */
  function rememberFrontrowProps() {
    PIN_PROPS.forEach((prop) => {
      if (bubble.style.getPropertyPriority(prop) !== 'important') {
        savedProps[prop] = bubble.style.getPropertyValue(prop);
      }
    });
  }

  function unpin() {
    if (!bubble || !pinned) return;
    PIN_PROPS.forEach((prop) => {
      if (bubble.style.getPropertyPriority(prop) === 'important') {
        const value = savedProps[prop];
        if (value) bubble.style.setProperty(prop, value);
        else bubble.style.removeProperty(prop);
      }
    });
    pinned = false;
  }

  /* Page coordinates of a point, relative to the real button's containing block. */
  function toContainingBlock(x, y) {
    let node = bubble.parentElement;
    while (node && node !== document.documentElement) {
      const cs = window.getComputedStyle(node);
      if (cs.position !== 'static' || cs.transform !== 'none') {
        const r = node.getBoundingClientRect();
        return {
          x: x - r.left - node.clientLeft + node.scrollLeft,
          y: y - r.top - node.clientTop + node.scrollTop
        };
      }
      node = node.parentElement;
    }
    return { x: x + window.scrollX, y: y + window.scrollY };
  }

  function placeBubble() {
    placeQueued = false;
    const usable = bubbleUsable();
    setRealSlots(usable);

    if (!usable) {
      if (bubble) {
        unpin();
        bubble.classList.remove(PARKED_CLASS);
      }
      return;
    }

    // Frontrow has opened the chat window: let it take the screen.
    if (isChatWindowSize(bubble)) {
      unpin();
      bubble.classList.remove(PARKED_CLASS);
      if (sheetContainer) closeSheet();
      return;
    }

    // Button-sized: learn its size so the spots match it.
    rememberFrontrowProps();
    const rect = bubble.getBoundingClientRect();
    if (rect.width > 20 && rect.height > 20) {
      bubbleSize = { w: Math.round(rect.width), h: Math.round(rect.height) };
    }

    const spots = getSpots();
    if (!hostSpot || spots.indexOf(hostSpot) === -1 || visibleRatio(hostSpot) === 0) {
      hostSpot = null;
      let best = 0;
      spots.forEach((spot) => {
        const ratio = visibleRatio(spot);
        if (ratio > best) {
          best = ratio;
          hostSpot = spot;
        }
      });
    }

    if (!hostSpot) {
      // Neither spot is on screen: keep the button out of sight.
      bubble.classList.add(PARKED_CLASS);
      return;
    }

    const r = hostSpot.getBoundingClientRect();
    const pos = toContainingBlock(r.left, r.top);

    setImportant(bubble, 'position', 'absolute');
    setImportant(bubble, 'left', Math.round(pos.x) + 'px');
    setImportant(bubble, 'top', Math.round(pos.y) + 'px');
    setImportant(bubble, 'right', 'auto');
    setImportant(bubble, 'bottom', 'auto');
    setImportant(bubble, 'margin', '0px');
    setImportant(bubble, 'z-index', PIN_Z_INDEX);
    pinned = true;
    bubble.classList.remove(PARKED_CLASS);
  }

  function queuePlace() {
    if (placeQueued) return;
    placeQueued = true;
    window.requestAnimationFrame(placeBubble);
  }

  /* ---------------------------- Fallback sheet ---------------------------- */

  function findWidgetContainer(root) {
    const own = root && root.querySelector('.frontrow-gpt-container');
    if (own && own.querySelector('iframe.iframe-frontrow-gpt')) return own;
    const any = document.querySelector('.frontrow-gpt-container iframe.iframe-frontrow-gpt');
    return any ? any.closest('.frontrow-gpt-container') : null;
  }

  /* The dimmed backdrop is a shadow on the widget itself (so it can never sit on
     top of the widget). Tapping outside the widget closes it. */
  function ensureCloseButton() {
    if (sheetClose) return sheetClose;

    sheetClose = document.createElement('button');
    sheetClose.type = 'button';
    sheetClose.className = SHEET_CLASS + '__close';
    sheetClose.setAttribute('aria-label', 'Close chat');
    sheetClose.innerHTML = '&times;';
    sheetClose.hidden = true;
    sheetClose.addEventListener('click', closeSheet);

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && sheetContainer) closeSheet();
    });

    document.addEventListener(
      'click',
      (event) => {
        if (!sheetContainer) return;
        const target = event.target;
        if (sheetContainer.contains(target) || target === sheetClose) return;
        if (target.closest && target.closest('[data-hpn-nad3-extra-strength-buy-box-clinician-ai-trigger]')) return;
        event.preventDefault();
        event.stopPropagation();
        closeSheet();
      },
      true
    );

    document.body.appendChild(sheetClose);
    return sheetClose;
  }

  /* If an ancestor turns position:fixed into something else (transform etc.),
     move the widget to <body> so it sits on the screen correctly. */
  function ensureSheetOnScreen(container) {
    const rect = container.getBoundingClientRect();
    const vh = window.innerHeight || document.documentElement.clientHeight;
    if (Math.abs(rect.bottom - vh) <= 2) return;

    sheetPlaceholder = document.createComment('hpn-clinician-ai-widget');
    container.parentNode.insertBefore(sheetPlaceholder, container);
    document.body.appendChild(container);
  }

  function openSheet(root) {
    const container = findWidgetContainer(root);
    if (!container) return false;

    ensureCloseButton().hidden = false;
    container.classList.add(SHEET_OPEN_CLASS);
    sheetContainer = container;
    ensureSheetOnScreen(container);

    document.documentElement.classList.add(SHEET_CLASS + '-lock');

    sheetClose.focus({ preventScroll: true });
    return true;
  }

  function closeSheet() {
    if (sheetClose) sheetClose.hidden = true;
    document.documentElement.classList.remove(SHEET_CLASS + '-lock');

    if (sheetContainer) {
      sheetContainer.classList.remove(SHEET_OPEN_CLASS);
      if (sheetPlaceholder && sheetPlaceholder.parentNode) {
        sheetPlaceholder.parentNode.insertBefore(sheetContainer, sheetPlaceholder);
        sheetPlaceholder.parentNode.removeChild(sheetPlaceholder);
      }
    }

    sheetContainer = null;
    sheetPlaceholder = null;
  }

  function openChat(root) {
    syncFloating();
    if (openEls.size) return; // Frontrow's chat window is already showing
    openSheet(root);
  }

  /* --------------------------- Rotating subtitle --------------------------- */

  function startRotation(seconds) {
    if (rotateTimer) return;

    const rotators = () => document.querySelectorAll('[data-hpn-nad3-extra-strength-buy-box-clinician-ai-rotator]');
    if (!rotators().length) return;

    let index = 0;
    const ms = Math.max(2, Number(seconds) || 3) * 1000;

    rotateTimer = window.setInterval(() => {
      index += 1;
      rotators().forEach((rotator) => {
        const items = rotator.children;
        if (items.length < 2) return;
        const active = index % items.length;
        for (let i = 0; i < items.length; i += 1) {
          items[i].classList.toggle('is-active', i === active);
        }
      });
    }, ms);
  }

  /* -------------------------------- Diagnostics -------------------------------- */

  function renderDebug(found) {
    let box = document.getElementById('hpn-fr-debug');
    if (!box) {
      box = document.createElement('pre');
      box.id = 'hpn-fr-debug';
      box.style.cssText =
        'position:fixed;left:0;right:0;top:0;z-index:2147483647;max-height:62vh;overflow:auto;margin:0;padding:6px;' +
        'background:rgba(0,0,0,.85);color:#0f0;font:10px/1.3 monospace;white-space:pre-wrap;pointer-events:none;';
      document.documentElement.appendChild(box);
    }
    const lines = found.map((el) => {
      const r = el.getBoundingClientRect();
      const cs = window.getComputedStyle(el);
      const cls = (typeof el.className === 'string' ? el.className.split(' ').filter((c) => c.indexOf('hpn-') !== 0).join('.') : '').slice(0, 50);
      return (
        el.tagName.toLowerCase() + (el.id ? '#' + el.id : '') + (cls ? '.' + cls : '') +
        ' ' + Math.round(r.width) + 'x' + Math.round(r.height) + '@' + Math.round(r.left) + ',' + Math.round(r.top) +
        ' ' + cs.position + '/' + cs.display + '/' + cs.visibility +
        (el.hasAttribute(OPEN_ATTR) ? ' OPEN' : '') + (el.classList.contains(HIDDEN_CLASS) ? ' HIDDEN' : '') +
        (el.classList.contains(PARKED_CLASS) ? ' PARKED' : '') +
        ' style="' + String(el.getAttribute('style') || '').slice(0, 160) + '"'
      );
    });
    box.textContent =
      'bubble=' + (bubble ? 'found' : 'none') + ' pinned=' + pinned + ' host=' +
      (hostSpot ? (hostSpot.closest('.hpn-nad3-extra-strength-buy-box__clinicianAiSlot--gallery') ? 'gallery' : 'offer') : 'none') +
      ' sheet=' + (sheetContainer ? 'open' : 'closed') + ' vw=' + window.innerWidth + ' vh=' + window.innerHeight +
      '\n-- messages (newest last) --\n' + (debugMessages.join('\n') || '(none)') +
      '\n-- elements --\n' + (lines.join('\n') || '(none)');
  }

  if (debug) {
    window.addEventListener('message', (event) => {
      if (String(event.origin).indexOf('frontrow') === -1) return;
      let data = '';
      try {
        data = typeof event.data === 'string' ? event.data : JSON.stringify(event.data);
      } catch (e) {
        data = String(event.data);
      }
      const from = Array.from(document.querySelectorAll('iframe')).find((f) => f.contentWindow === event.source);
      const fromName = from ? (from.id ? '#' + from.id : String(from.className).split(' ').slice(0, 3).join('.')) : '?';
      if (/RESIZE_HEIGHT/.test(data) && debugMessages.length && debugMessages[debugMessages.length - 1].indexOf('RESIZE_HEIGHT') !== -1) {
        debugMessages.pop();
      }
      debugMessages.push(new Date().toISOString().slice(14, 19) + ' ' + fromName + ': ' + String(data).slice(0, 200));
      if (debugMessages.length > 16) debugMessages.shift();
      scheduleSync();
    });
  }

  /* ---------------------------------- Setup ---------------------------------- */

  function bindRoot(root) {
    const triggers = root.querySelectorAll('[data-hpn-nad3-extra-strength-buy-box-clinician-ai-trigger]');
    if (!triggers.length) return false;

    if (root.getAttribute('data-hpn-nad3-extra-strength-buy-box-clinician-ai-hide-floating') === '1') {
      hideFloating = true;
    }

    triggers.forEach((trigger) => {
      if (trigger.__hpnNad3ExtraStrengthClinicianAiBound) return;
      trigger.__hpnNad3ExtraStrengthClinicianAiBound = true;

      trigger.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        openChat(root);
      });
    });

    startRotation(root.getAttribute('data-hpn-nad3-extra-strength-buy-box-clinician-ai-rotate-seconds'));
    return true;
  }

  function init() {
    let found = false;
    document.querySelectorAll(SELECTOR_ROOT).forEach((root) => {
      if (bindRoot(root)) found = true;
    });
    hasSlots = found;
    if (hasSlots || debug) watchFloating();
  }

  // Switching between mobile and desktop widths.
  const mq = window.matchMedia(MOBILE_QUERY);
  const onMqChange = () => {
    if (!isMobile() && sheetContainer) closeSheet();
    hostSpot = null;
    queuePlace();
  };
  if (mq.addEventListener) mq.addEventListener('change', onMqChange);

  const scheduleInit = () => window.requestAnimationFrame(init);

  document.addEventListener('shopify:section:load', scheduleInit);
  document.addEventListener('shopify:section:reorder', scheduleInit);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', scheduleInit, { once: true });
  } else {
    scheduleInit();
  }
})();
